import { useState, useEffect } from 'react'
import { supabase } from './supabase'
import ScorecardMatrix from './ScorecardMatrix'

// ─── PAYOUT CALCULATORS ───────────────────────────────────────────────────────

function getNetScores(holeScores, holePars, handicapIndex, numHoles) {
  if (!handicapIndex) return holeScores
  const strokesPerHole = Array(numHoles).fill(0)
  let remaining = Math.round(handicapIndex)
  let i = 0
  while (remaining > 0) { strokesPerHole[i % numHoles]++; remaining--; i++ }
  return holeScores.map((s, i) => s != null ? Math.max(1, s - strokesPerHole[i]) : null)
}

function calcStrokePlay(line, scorecards, handicaps, holePars) {
  const numHoles = holePars.length
  const totals = {}
  line.player_ids.forEach(pid => {
    const sc = scorecards[pid] || []
    const scores = line.scoring === 'net' ? getNetScores(sc, holePars, handicaps[pid] != null ? handicaps[pid] : null, numHoles) : sc
    totals[pid] = scores.reduce((a, b) => b != null ? a + b : a, 0)
  })
  const sorted = [...line.player_ids].sort((a, b) => totals[a] - totals[b])
  const winner = sorted[0]
  const pot = line.wager_amount * line.player_ids.length
  const payouts = {}
  line.player_ids.forEach(pid => { payouts[pid] = pid === winner ? pot - line.wager_amount : -line.wager_amount })
  return { payouts, totals, winner, detail: `$${line.wager_amount}/player · Winner takes $${pot}` }
}

function calcSkins(line, scorecards, handicaps, holePars) {
  const numHoles = holePars.length
  const skinVal = line.skin_value || line.wager_amount
  const skinsWon = {}
  line.player_ids.forEach(pid => { skinsWon[pid] = 0 })
  let carryOver = 0
  const holeResults = []
  for (let h = 0; h < numHoles; h++) {
    const holeScores = {}
    line.player_ids.forEach(pid => {
      const sc = scorecards[pid] || []
      const gross = sc[h]
      if (gross == null) { holeScores[pid] = null; return }
      holeScores[pid] = line.scoring === 'net' && handicaps[pid] != null ? Math.max(1, gross - Math.round(handicaps[pid] / numHoles)) : gross
    })
    const valid = line.player_ids.filter(pid => holeScores[pid] != null)
    if (valid.length === 0) { holeResults.push({ hole: h+1, winner: null, skins: 0 }); continue }
    const minScore = Math.min(...valid.map(pid => holeScores[pid]))
    const winners = valid.filter(pid => holeScores[pid] === minScore)
    if (winners.length === 1) {
      const skinsThisHole = 1 + carryOver
      skinsWon[winners[0]] += skinsThisHole
      holeResults.push({ hole: h+1, winner: winners[0], skins: skinsThisHole, carry: carryOver > 0 })
      carryOver = 0
    } else {
      if (h === numHoles - 1 && carryOver > 0) {
        const split = (1 + carryOver) / winners.length
        winners.forEach(w => { skinsWon[w] += split })
        holeResults.push({ hole: h+1, winner: 'split', skins: 1 + carryOver })
      } else {
        carryOver++
        holeResults.push({ hole: h+1, winner: 'tie', skins: 0, carry: true })
      }
    }
  }
  const payouts = {}
  line.player_ids.forEach(pid => { payouts[pid] = (skinsWon[pid] * skinVal) - line.wager_amount })
  return { payouts, skinsWon, holeResults, detail: `$${skinVal}/skin · ${Object.values(skinsWon).reduce((a,b)=>a+b,0)} skins played` }
}

function calcNassau(line, scorecards, handicaps, holePars) {
  const numHoles = holePars.length
  const half = Math.floor(numHoles / 2)
  const segmentWager = line.wager_amount / 3
  const getTotal = (pid, from, to) => {
    const sc = scorecards[pid] || []
    const scores = line.scoring === 'net' ? getNetScores(sc, holePars, handicaps[pid] != null ? handicaps[pid] : null, numHoles) : sc
    return scores.slice(from, to).reduce((a, b) => b != null ? a + b : a, 0)
  }
  const segments = [
    { label: `Front ${half}`, from: 0, to: half },
    { label: `Back ${numHoles - half}`, from: half, to: numHoles },
    { label: 'Total', from: 0, to: numHoles },
  ]
  const payouts = {}
  line.player_ids.forEach(pid => { payouts[pid] = 0 })
  const segmentResults = []
  segments.forEach(seg => {
    const totals = {}
    line.player_ids.forEach(pid => { totals[pid] = getTotal(pid, seg.from, seg.to) })
    const minScore = Math.min(...line.player_ids.map(pid => totals[pid]))
    const winners = line.player_ids.filter(pid => totals[pid] === minScore)
    const prize = (segmentWager * line.player_ids.length) / winners.length
    line.player_ids.forEach(pid => {
      payouts[pid] += winners.includes(pid) ? prize - segmentWager : -segmentWager
    })
    segmentResults.push({ label: seg.label, winners, totals })
  })
  return { payouts, segmentResults, detail: `$${segmentWager.toFixed(0)}/segment · 3 bets in 1` }
}

export function calculatePayouts(line, scorecards, handicaps, holePars) {
  if (line.format === 'stroke_play') return calcStrokePlay(line, scorecards, handicaps, holePars)
  if (line.format === 'skins') return calcSkins(line, scorecards, handicaps, holePars)
  if (line.format === 'nassau') return calcNassau(line, scorecards, handicaps, holePars)
  return { payouts: {}, detail: '' }
}

// ─── FORMAT INFO ──────────────────────────────────────────────────────────────

const FORMAT_INFO = {
  stroke_play: { label: 'STROKE PLAY', icon: '🏌️', desc: 'Lowest total strokes wins the pot' },
  skins: { label: 'SKINS', icon: '🎯', desc: 'Win holes outright — ties carry over' },
  nassau: { label: 'NASSAU', icon: '3️⃣', desc: 'Front 9 · Back 9 · Total — 3 separate bets' },
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────

export default function BettingLines({ session, match, players, profiles, scorecards, handicaps, fullScorecards }) {
  const [lines, setLines] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [accepting, setAccepting] = useState({})
  const [expanded, setExpanded] = useState({})
  const [settling, setSettling] = useState({})
  const [toast, setToast] = useState(null)

  // Form state
  const [format, setFormat] = useState('stroke_play')
  const [scoring, setScoring] = useState('gross')
  const [wagerAmount, setWagerAmount] = useState('')
  const [skinValue, setSkinValue] = useState('')
  const [pressEnabled, setPressEnabled] = useState(false)
  const [selectedPlayers, setSelectedPlayers] = useState([])

  const showToast = (msg, type = 'info') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3000)
  }

  const allPlayerIds = [session.user.id, ...players.map(p => p.player_id)]
  const acceptedPlayerIds = [session.user.id, ...players.filter(p => p.status === 'accepted').map(p => p.player_id)]

  useEffect(() => { loadLines() }, [match.id])

  const loadLines = async () => {
    setLoading(true)
    const { data } = await supabase.from('betting_lines').select('*').eq('match_id', match.id).order('created_at', { ascending: true })
    setLines(data || [])
    setLoading(false)
  }

  const togglePlayer = (pid) => {
    setSelectedPlayers(prev => prev.includes(pid) ? prev.filter(p => p !== pid) : [...prev, pid])
  }

  const handleProposeLine = async () => {
    // Check handicap if net scoring
    if (scoring === 'net' && handicaps[session.user.id] == null) {
      showToast('You need a handicap index to propose a net bet — update your Golf Profile first', 'error')
      return
    }
    if (selectedPlayers.length < 2) return showToast('Select at least 2 players', 'error')
    if (!selectedPlayers.includes(session.user.id)) return showToast('You must be part of the bet', 'error')
    const amt = parseFloat(format === 'skins' ? skinValue : wagerAmount)
    if (!amt || amt <= 0) return showToast('Enter a valid wager amount', 'error')
    setSaving(true)
    const { error } = await supabase.from('betting_lines').insert({
      match_id: match.id,
      format,
      scoring,
      wager_amount: amt,
      skin_value: format === 'skins' ? amt : null,
      press_enabled: format === 'nassau' ? pressEnabled : false,
      player_ids: selectedPlayers,
      accepted_by: [session.user.id],
      created_by: session.user.id,
      status: selectedPlayers.length === 1 ? 'active' : 'pending',
    })
    setSaving(false)
    if (error) return showToast(error.message, 'error')
    showToast('Betting line proposed — waiting for others to accept', 'success')
    setCreating(false)
    setSelectedPlayers([])
    setWagerAmount('')
    setSkinValue('')
    loadLines()
  }

  const handleAccept = async (line) => {
    // Check handicap if net scoring
    if (line.scoring === 'net' && handicaps[session.user.id] == null) {
      showToast('You need a handicap index to join a net bet — update your Golf Profile first', 'error')
      return
    }
    setAccepting(prev => ({ ...prev, [line.id]: true }))
    const newAccepted = [...(line.accepted_by || []), session.user.id]
    const allAccepted = line.player_ids.every(pid => newAccepted.includes(pid))
    const { error } = await supabase.from('betting_lines').update({
      accepted_by: newAccepted,
      status: allAccepted ? 'active' : 'pending',
    }).eq('id', line.id)
    setAccepting(prev => ({ ...prev, [line.id]: false }))
    if (error) return showToast(error.message, 'error')
    showToast(allAccepted ? 'Betting line is now active!' : 'Accepted — waiting for others', 'success')
    loadLines()
  }

  const handleDecline = async (line) => {
    const { error } = await supabase.from('betting_lines').update({ status: 'declined' }).eq('id', line.id)
    if (error) return showToast(error.message, 'error')
    showToast('Betting line declined', 'info')
    loadLines()
  }


  const handleDispute = async (line) => {
    await supabase.from('betting_lines').update({ status: 'disputed' }).eq('id', line.id)
    showToast('Calculation dispute raised — support will review', 'info')
    loadLines()
  }

  const handleSettle = async (line) => {
    setSettling(prev => ({ ...prev, [line.id]: true }))
    const newSettled = [...new Set([...(line.settled_by || []), session.user.id])]
    const allSettled = line.player_ids.every(pid => newSettled.includes(pid))
    const result = getResult(line)

    await supabase.from('betting_lines').update({
      settled_by: newSettled,
      status: allSettled ? 'settled' : 'active',
      payouts: allSettled ? result.payouts : line.payouts,
    }).eq('id', line.id)

    setSettling(prev => ({ ...prev, [line.id]: false }))
    if (allSettled) showToast('Bet settled! Payouts confirmed.', 'success')
    else showToast('Confirmed — waiting for others to settle', 'success')
    loadLines()
  }

  const toggleExpand = (lineId) => {
    setExpanded(prev => ({ ...prev, [lineId]: !prev[lineId] }))
  }

  const getResult = (line) => {
    const sc = {}
    allPlayerIds.forEach(pid => { sc[pid] = scorecards[pid] || [] })
    return calculatePayouts(line, sc, handicaps || {}, match.hole_pars || [])
  }

  const fmt = (pid) => profiles[pid] ? `${profiles[pid].first_name} ${profiles[pid].last_name || ''}`.trim() : '?'
  const holesPlayedByPlayer = (pid) => (scorecards[pid] || []).filter(s => s != null).length

  if (loading) return (
    <div style={{ padding: '20px 0', textAlign: 'center' }}>
      <div style={{ color: '#7A7570', fontSize: 13 }}>Loading betting lines...</div>
    </div>
  )

  const pendingLines = lines.filter(l => l.status === 'pending' && l.player_ids.includes(session.user.id) && !(l.accepted_by || []).includes(session.user.id))
  const activeLines = lines.filter(l => l.status === 'active')
  const settledLines = lines.filter(l => l.status === 'settled')
  const myPendingProposals = lines.filter(l => l.status === 'pending' && l.created_by === session.user.id)

  return (
    <div style={s.wrap}>
      {toast && <div style={{ ...s.toast, background: toast.type === 'error' ? '#C0392B' : toast.type === 'success' ? '#00563F' : '#1A1814' }}>{toast.msg}</div>}

      {/* Pending invites to bet */}
      {pendingLines.map(line => {
        const info = FORMAT_INFO[line.format]
        const proposer = profiles[line.created_by]
        return (
          <div key={line.id} style={s.pendingCard}>
            <div style={s.pendingHeader}>
              <span style={s.pendingIcon}>💬</span>
              <div style={s.pendingInfo}>
                <div style={s.pendingTitle}>{proposer?.first_name} proposed a {info.label} bet</div>
                <div style={s.pendingDesc}>{info.icon} {info.desc}</div>
              </div>
            </div>
            <div style={s.pendingDetails}>
              <div style={s.pendingDetail}>
                <span style={s.pendingDetailLabel}>WAGER </span>
                <span style={s.pendingDetailVal}>${line.wager_amount}{line.format === 'skins' ? '/skin' : '/player'}</span>
              </div>
              <div style={s.pendingDetail}>
                <span style={s.pendingDetailLabel}>SCORING </span>
                <span style={s.pendingDetailVal}>{line.scoring.toUpperCase()}</span>
              </div>
              <div style={s.pendingDetail}>
                <span style={s.pendingDetailLabel}>PLAYERS </span>
                <span style={s.pendingDetailVal}>{line.player_ids.map(pid => profiles[pid]?.first_name || '?').join(', ')}</span>
              </div>
            </div>
            <div style={s.pendingActions}>
              <button style={{ ...s.acceptBtn, opacity: accepting[line.id] ? 0.7 : 1 }}
                onClick={() => handleAccept(line)} disabled={accepting[line.id]}>
                {accepting[line.id] ? '...' : '✓ Accept'}
              </button>
              <button style={s.declineBtn} onClick={() => handleDecline(line)}>Decline</button>
            </div>
          </div>
        )
      })}

      {/* My pending proposals */}
      {myPendingProposals.map(line => {
        const info = FORMAT_INFO[line.format]
        const waiting = line.player_ids.filter(pid => !(line.accepted_by || []).includes(pid))
        return (
          <div key={line.id} style={s.proposalCard}>
            <div style={s.proposalHeader}>
              <span>{info.icon}</span>
              <div style={s.proposalInfo}>
                <div style={s.proposalTitle}>{info.label} — Awaiting acceptance</div>
                <div style={s.proposalSub}>Waiting for: {waiting.map(pid => profiles[pid]?.first_name || '?').join(', ')}</div>
              </div>
              <div style={s.proposalAmt}>${line.wager_amount}</div>
            </div>
          </div>
        )
      })}

      {/* Active lines */}
      {activeLines.map(line => {
        const info = FORMAT_INFO[line.format]
        const result = getResult(line)
        const holesPlayed = Math.min(...line.player_ids.map(pid => holesPlayedByPlayer(pid)))
        const numHoles = match.num_holes || 18
        const isComplete = line.player_ids.every(pid => {
          const sc = fullScorecards?.[pid]
          return sc && (sc.status === 'closed' || sc.status === 'review')
        })
        const allAgreed = line.player_ids.every(pid => {
          const sc = fullScorecards?.[pid]
          return sc && (sc.agreed_by || []).includes(pid)
        })

        // Quick standing summary
          const leader = [...line.player_ids].sort((a, b) => {
            const pa = result.payouts?.[a] ?? 0
            const pb = result.payouts?.[b] ?? 0
            return pb - pa
          })
          const standingSummary = leader.map(pid => {
            const name = profiles[pid] ? `${profiles[pid].first_name} ${profiles[pid].last_name || ''}`.trim() : '?'
            const sc = scorecards[pid] || []
            const total = sc.reduce((a, b) => b != null ? a + b : a, 0)
            const par = (match.hole_pars || []).reduce((a, b) => a + b, 0)
            const played = sc.filter(s => s != null).length
            if (!played) return `${name} —`
            const diff = total - par
            const vp = diff > 0 ? '+' + diff : diff === 0 ? 'E' : String(diff)
            const payout = result.payouts?.[pid] ?? 0
            const payStr = payout >= 0 ? `+$${payout.toFixed(0)}` : `-$${Math.abs(payout).toFixed(0)}`
            return `${name} ${vp} (${payStr})`
          }).join(' · ')

          const iHaveSettled = (line.settled_by || []).includes(session.user.id)
          const settledCount = (line.settled_by || []).filter(pid => line.player_ids.includes(pid)).length
          const allSettled = line.player_ids.every(pid => (line.settled_by || []).includes(pid))
          const showSettle = (isComplete || allAgreed) && !allSettled

          return (
          <div key={line.id} style={s.lineCard}>
            {/* Row 1 — format + actions */}
            <div style={s.lineRow1}>
              <span style={s.lineIcon}>{info.icon}</span>
              <div style={s.lineTitle}>{info.label} · {line.scoring.toUpperCase()} · ${line.wager_amount}{line.format === 'skins' ? '/skin' : '/pl'}</div>
              <div style={s.lineActions}>
                {showSettle && !iHaveSettled && (
                  <button style={{ ...s.settleInlineBtn, opacity: settling[line.id] ? 0.7 : 1 }}
                    onClick={() => handleSettle(line)} disabled={settling[line.id]}>
                    {settling[line.id] ? '...' : `SETTLE ${settledCount}/${line.player_ids.length}`}
                  </button>
                )}
                {showSettle && !iHaveSettled && (
                  <button style={s.disputeInlineBtn} onClick={() => handleDispute(line)}>⚑</button>
                )}
                {iHaveSettled && !allSettled && <span style={s.waitingInline}>✓ {settledCount}/{line.player_ids.length}</span>}
                {allSettled && <span style={s.paidInline}>✓ PAID</span>}
                {!showSettle && (
                  <span style={{ ...s.linePill, background: isComplete ? 'rgba(0,96,57,0.1)' : 'rgba(201,168,76,0.1)', color: isComplete ? '#00563F' : '#9B7D52' }}>
                    {isComplete ? 'CLOSED' : `${holesPlayed}/${numHoles}`}
                  </span>
                )}
              </div>
            </div>

            {/* Row 2 — quick standing */}
            <div style={s.lineRow2}>{standingSummary}</div>

            {/* Nassau segments */}
            {line.format === 'nassau' && result.segmentResults && (
              <div style={s.segmentRow}>
                {result.segmentResults.map((seg, i) => (
                  <div key={i} style={s.segmentCell}>
                    <div style={s.segmentLabel}>{seg.label}</div>
                    <div style={s.segmentWinner}>
                      {seg.winners?.length ? seg.winners.map(w => profiles[w]?.first_name || '?').join('/') : '—'}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Skins hole by hole */}
            {line.format === 'skins' && result.holeResults && (
              <div style={{ overflowX: 'auto' }}>
                <div style={s.skinsRow}>
                  {result.holeResults.map((hr, i) => (
                    <div key={i} style={s.skinCell}>
                      <div style={s.skinHole}>{hr.hole}</div>
                      <div style={{ ...s.skinResult, color: hr.winner === 'tie' ? '#C19A6B' : hr.winner && hr.winner !== 'split' ? '#00563F' : '#ccc' }}>
                        {hr.winner === 'tie' ? '→' : hr.winner === 'split' ? '½' : hr.winner ? profiles[hr.winner]?.first_name?.charAt(0) || '✓' : '—'}
                      </div>
                      {hr.skins > 1 && <div style={s.skinCarry}>×{hr.skins}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button style={s.expandBtn} onClick={() => toggleExpand(line.id)}>
              {expanded[line.id] ? '▲ hide' : '▼ details'}
            </button>

            {expanded[line.id] && (
              <div style={s.expandedMatrix}>
                <ScorecardMatrix
                  match={match}
                  players={line.player_ids}
                  profiles={profiles}
                  scorecards={scorecards}
                  highlightPlayer={session.user.id}
                />
                {/* Payout breakdown — light */}
                <div style={s.payoutBreakdown}>
                  <div style={s.payoutBreakdownTitle}>PAYOUT</div>
                  {line.player_ids.map(pid => {
                    const payout = result.payouts?.[pid] ?? 0
                    return (
                      <div key={pid} style={s.payoutRow}>
                        <div style={s.payoutName}>{fmt(pid)}{pid === session.user.id ? ' (you)' : ''}</div>
                        <div style={{ ...s.payoutAmt, color: payout >= 0 ? '#00563F' : '#C0392B' }}>
                          {payout >= 0 ? '+$' : '-$'}{Math.abs(payout).toFixed(2)}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

          </div>
        )
      })}

      {/* Settled lines */}
      {settledLines.map(line => {
        const info = FORMAT_INFO[line.format]
        const result = getResult(line)
        return (
          <div key={line.id} style={s.settledCard}>
            <div style={s.lineHeader}>
              <span style={s.lineIcon}>{info.icon}</span>
              <div style={s.lineInfo}>
                <div style={s.lineLabel}>{info.label}</div>
                <div style={s.lineSub}>SETTLED</div>
              </div>
              <div style={{ ...s.linePill, background: 'rgba(0,96,57,0.1)', color: '#00563F' }}>✓ PAID</div>
            </div>
            <div style={s.standings}>
              {line.player_ids.map(pid => {
                const payout = result.payouts?.[pid] ?? 0
                return (
                  <div key={pid} style={s.standingRow}>
                    <div style={s.standingName}>{fmt(pid)}{pid === session.user.id ? ' (you)' : ''}</div>
                    <div style={{ ...s.standingPayout, color: payout >= 0 ? '#00563F' : '#C0392B' }}>
                      {payout >= 0 ? '+$' : '-$'}{Math.abs(payout).toFixed(2)}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}

      {/* Empty state */}
      {lines.length === 0 && !creating && (
        <div style={s.empty}>
          <div style={s.emptyIcon}>💰</div>
          <div style={s.emptyText}>No betting lines yet</div>
          <div style={s.emptySub}>Propose a line to start betting with your group</div>
        </div>
      )}

      {/* Create form */}
      {creating ? (
        <div style={s.createCard}>
          <div style={s.createTitle}>PROPOSE A BET</div>

          <div style={s.fieldLabel}>FORMAT</div>
          <div style={s.formatRow}>
            {Object.entries(FORMAT_INFO).map(([key, info]) => (
              <button key={key} onClick={() => setFormat(key)}
                style={{ ...s.formatBtn, ...(format === key ? s.formatBtnActive : {}) }}>
                <div style={{ fontSize: 24 }}>{info.icon}</div>
                <div style={s.formatBtnLabel}>{info.label}</div>
              </button>
            ))}
          </div>

          <div style={s.fieldLabel}>SCORING</div>
          <div style={s.toggleRow}>
            <button onClick={() => setScoring('gross')} style={{ ...s.toggleBtn, ...(scoring === 'gross' ? s.toggleBtnActive : {}) }}>Gross</button>
            <button onClick={() => setScoring('net')} style={{ ...s.toggleBtn, ...(scoring === 'net' ? s.toggleBtnActive : {}) }}>Net (Handicap)</button>
          </div>

          <div style={s.fieldLabel}>WHO'S IN THIS BET</div>
          <div style={s.playerList}>
            {acceptedPlayerIds.map(pid => (
              <button key={pid} onClick={() => togglePlayer(pid)}
                style={{ ...s.playerBtn, ...(selectedPlayers.includes(pid) ? s.playerBtnActive : {}) }}>
                <div style={s.playerBtnName}>{fmt(pid)}{pid === session.user.id ? ' (you)' : ''}</div>
                <div style={{ color: '#00563F', fontWeight: 700 }}>{selectedPlayers.includes(pid) ? '✓' : ''}</div>
              </button>
            ))}
          </div>

          <div style={s.fieldLabel}>
            {format === 'skins' ? 'VALUE PER SKIN ($)' : 'WAGER PER PLAYER ($)'}
          </div>
          <input style={s.input} type="number" min="0" step="0.5"
            placeholder={format === 'skins' ? 'e.g. 5 per skin' : 'e.g. 20 per player'}
            value={format === 'skins' ? skinValue : wagerAmount}
            onChange={e => format === 'skins' ? setSkinValue(e.target.value) : setWagerAmount(e.target.value)}
          />

          {format === 'nassau' && (
            <>
              <div style={s.fieldLabel}>PRESS</div>
              <div style={s.toggleRow}>
                <button onClick={() => setPressEnabled(false)} style={{ ...s.toggleBtn, ...(!pressEnabled ? s.toggleBtnActive : {}) }}>No Press</button>
                <button onClick={() => setPressEnabled(true)} style={{ ...s.toggleBtn, ...(pressEnabled ? s.toggleBtnActive : {}) }}>Press Enabled</button>
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button style={{ ...s.saveBtn, opacity: saving ? 0.7 : 1 }} onClick={handleProposeLine} disabled={saving}>
              {saving ? 'Sending...' : 'Propose Bet →'}
            </button>
            <button style={s.cancelBtn} onClick={() => { setCreating(false); setSelectedPlayers([]) }}>Cancel</button>
          </div>
        </div>
      ) : (
        <button style={s.addBtn} onClick={() => { setCreating(true); setSelectedPlayers([session.user.id]) }}>
          + Propose Betting Line
        </button>
      )}
    </div>
  )
}

const s = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 10 },
  toast: { position: 'fixed', top: 0, left: 0, right: 0, padding: '16px 24px', color: 'white', fontSize: 14, fontWeight: 600, textAlign: 'center', zIndex: 100 },
  pendingCard: { background: '#0A0A0A', borderRadius: 20, padding: 18, border: '1.5px solid #C19A6B', display: 'flex', flexDirection: 'column', gap: 12 },
  pendingHeader: { display: 'flex', gap: 12, alignItems: 'flex-start' },
  pendingIcon: { fontSize: 24, flexShrink: 0 },
  pendingInfo: { flex: 1 },
  pendingTitle: { fontSize: 14, fontWeight: 700, color: '#FDF9E7' },
  pendingDesc: { fontSize: 12, color: '#555', marginTop: 2 },
  pendingDetails: { display: 'flex', flexDirection: 'column', gap: 4 },
  pendingDetail: { fontSize: 13, color: '#FDF9E7' },
  pendingDetailLabel: { fontSize: 10, fontWeight: 700, color: '#555', letterSpacing: 1.2 },
  pendingDetailVal: { fontWeight: 600 },
  pendingActions: { display: 'flex', gap: 10 },
  acceptBtn: { flex: 1, background: 'linear-gradient(135deg,#00563F,#006B4D)', color: 'white', border: 'none', borderRadius: 12, padding: '12px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  declineBtn: { flex: 1, background: 'transparent', color: '#7A7570', border: '1px solid #333', borderRadius: 12, padding: '12px', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' },
  proposalCard: { background: 'rgba(201,168,76,0.06)', borderRadius: 16, padding: '14px 16px', border: '1px solid rgba(201,168,76,0.2)' },
  proposalHeader: { display: 'flex', alignItems: 'center', gap: 10 },
  proposalInfo: { flex: 1 },
  proposalTitle: { fontSize: 13, fontWeight: 700, color: '#1A1814' },
  proposalSub: { fontSize: 12, color: '#7A7570', marginTop: 2 },
  proposalAmt: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: '#C19A6B' },
  lineCard: { background: '#FFFFFF', borderRadius: 20, padding: 18, border: '1px solid #E0DDD4', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: 10 },
  settledCard: { background: '#FAFAF8', borderRadius: 20, padding: 18, border: '1px solid #E0DDD4', display: 'flex', flexDirection: 'column', gap: 10, opacity: 0.8 },
  lineHeader: { display: 'flex', alignItems: 'center', gap: 10 },
  lineIcon: { fontSize: 26, flexShrink: 0 },
  lineInfo: { flex: 1 },
  lineLabel: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, color: '#1A1814', letterSpacing: 1 },
  lineSub: { fontSize: 11, color: '#7A7570', fontWeight: 600, letterSpacing: 0.5 },
  linePill: { fontSize: 10, fontWeight: 700, borderRadius: 8, padding: '3px 10px', letterSpacing: 1 },
  standings: { display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px solid #EDE8D8', paddingTop: 10 },
  standingRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  standingLeft: {},
  standingName: { fontSize: 14, fontWeight: 600, color: '#1A1814' },
  standingHoles: { fontSize: 11, color: '#7A7570' },
  standingPayout: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, fontWeight: 700 },
  segmentRow: { display: 'flex', gap: 8, borderTop: '1px solid #EDE8D8', paddingTop: 10 },
  segmentCell: { flex: 1, textAlign: 'center' },
  segmentLabel: { fontSize: 10, color: '#7A7570', fontWeight: 700, letterSpacing: 1 },
  segmentWinner: { fontSize: 13, fontWeight: 700, color: '#00563F', marginTop: 2 },
  skinsRow: { display: 'flex', gap: 4, minWidth: 'max-content', paddingBottom: 4 },
  skinCell: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 },
  skinHole: { fontSize: 9, color: '#7A7570', fontWeight: 700 },
  skinResult: { width: 28, height: 28, borderRadius: 6, background: '#FDF9E7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 },
  skinCarry: { fontSize: 9, color: '#C19A6B', fontWeight: 700 },
  payoutSection: { background: '#0A0A0A', borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 8 },
  payoutTitle: { fontSize: 11, fontWeight: 700, color: '#444', letterSpacing: 1.5 },
  payoutRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  payoutName: { fontSize: 13, color: '#1A1814', fontWeight: 500 },
  payoutAmt: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 22 },
  disputeBtn: { flex: 1, background: 'transparent', border: '1.5px solid #C0392B', borderRadius: 12, padding: '13px', fontSize: 13, fontWeight: 700, color: '#C0392B', cursor: 'pointer', fontFamily: 'inherit', marginTop: 4 },
  settleBtn: { background: 'linear-gradient(135deg,#C19A6B,#9B7D52)', color: 'white', border: 'none', borderRadius: 12, padding: '13px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginTop: 4, textAlign: 'center' },
  settledBadge: { background: 'rgba(0,96,57,0.08)', border: '1px solid rgba(0,96,57,0.2)', borderRadius: 10, padding: '10px', fontSize: 13, color: '#00563F', fontWeight: 700, textAlign: 'center', marginTop: 4 },
  waitingSettle: { fontSize: 12, color: '#00563F', fontWeight: 600, textAlign: 'center', marginTop: 4 },
  confirmBtn: { background: 'linear-gradient(135deg,#C19A6B,#9B7D52)', color: 'white', border: 'none', borderRadius: 12, padding: '12px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginTop: 4 },
  empty: { background: '#FFFFFF', borderRadius: 20, padding: 36, border: '1px solid #E0DDD4', textAlign: 'center' },
  emptyIcon: { fontSize: 36, marginBottom: 10 },
  emptyText: { fontSize: 15, fontWeight: 700, color: '#1A1814', marginBottom: 4 },
  emptySub: { fontSize: 13, color: '#7A7570' },
  createCard: { background: '#FFFFFF', borderRadius: 20, padding: 20, border: '1px solid #E0DDD4', display: 'flex', flexDirection: 'column', gap: 12 },
  createTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: '#1A1814', letterSpacing: 1 },
  fieldLabel: { fontSize: 11, fontWeight: 700, color: '#7A7570', letterSpacing: 1.5, textTransform: 'uppercase' },
  formatRow: { display: 'flex', gap: 8 },
  formatBtn: { flex: 1, padding: '12px 6px', background: '#FDF9E7', border: '1.5px solid #E0DDD4', borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  formatBtnActive: { background: 'rgba(0,96,57,0.06)', border: '1.5px solid #00563F' },
  formatBtnLabel: { fontSize: 9, fontWeight: 700, color: '#1A1814', letterSpacing: 0.5 },
  toggleRow: { display: 'flex', gap: 8 },
  toggleBtn: { flex: 1, padding: '10px', background: '#FDF9E7', border: '1.5px solid #E0DDD4', borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: '#7A7570' },
  toggleBtnActive: { background: '#00563F', border: '1.5px solid #00563F', color: 'white' },
  playerList: { display: 'flex', flexDirection: 'column', gap: 6 },
  playerBtn: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: '#FDF9E7', border: '1.5px solid #E0DDD4', borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit' },
  playerBtnActive: { background: 'rgba(0,96,57,0.06)', border: '1.5px solid #00563F' },
  playerBtnName: { fontSize: 14, fontWeight: 600, color: '#1A1814' },
  input: { background: '#FDF9E7', border: '1.5px solid #E0DDD4', borderRadius: 12, padding: '12px 14px', fontSize: 14, color: '#1A1814', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' },
  saveBtn: { flex: 1, background: 'linear-gradient(135deg,#00563F,#006B4D)', color: 'white', border: 'none', borderRadius: 12, padding: '13px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  cancelBtn: { background: 'transparent', border: '1.5px solid #E0DDD4', borderRadius: 12, padding: '13px 20px', fontSize: 14, color: '#7A7570', cursor: 'pointer', fontFamily: 'inherit' },
  lineRow1: { display: 'flex', alignItems: 'center', gap: 8 },
  lineRow2: { fontSize: 12, color: '#7A7570', fontWeight: 500, paddingLeft: 2 },
  lineTitle: { flex: 1, fontSize: 12, fontWeight: 700, color: '#1A1814', letterSpacing: 0.3 },
  lineActions: { display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 },
  settleInlineBtn: { background: 'linear-gradient(135deg,#C19A6B,#9B7D52)', color: 'white', border: 'none', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  disputeInlineBtn: { background: 'transparent', border: '1px solid #C0392B', borderRadius: 8, padding: '5px 8px', fontSize: 11, color: '#C0392B', cursor: 'pointer', fontFamily: 'inherit' },
  waitingInline: { fontSize: 11, fontWeight: 700, color: '#00563F' },
  paidInline: { fontSize: 11, fontWeight: 700, color: '#00563F', background: 'rgba(0,96,57,0.1)', borderRadius: 6, padding: '3px 8px' },
  payoutBreakdown: { borderTop: '1px solid #EDE8D8', paddingTop: 10, marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8, background: '#FAFAF8', borderRadius: 10, padding: 12 },
  payoutBreakdownTitle: { fontSize: 10, fontWeight: 700, color: '#7A7570', letterSpacing: 1.5 },
  expandBtn: { background: 'transparent', border: 'none', color: '#00563F', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: '4px 0', letterSpacing: 0.5 },
  expandedMatrix: { borderTop: '1px solid #EDE8D8', paddingTop: 12 },
  addBtn: { background: 'transparent', border: '1.5px dashed #00563F', borderRadius: 14, padding: '16px', fontSize: 14, fontWeight: 700, color: '#00563F', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center' },
}
